import type { CaptainListItem } from './ship.models';

/** One captain’s assignment window from `CaptainCrewIdTimeline`. */
export interface CaptainCrewIdTimelineEntry {
  start_date?: string | null;
  end_date?: string | null;
}

/** `DimCaptainId` → assignment window (`YYYY-MM-DD` strings from Databricks). */
export type CaptainCrewIdTimeline = Readonly<Record<string, CaptainCrewIdTimelineEntry>>;

/** Roster entry under each voyage’s `captains[]` (multi-captain voyages). */
export interface VoyageContributingCaptain {
  captainId: string;
  name: string;
  nationality: string;
  manningAgent: string;
  /** `1` = current captain; higher = next in rotation; `null` sorts last. */
  order: number | null;
}

/** `perCaptain` entry when `shape === "perCaptainScalar"`. */
export interface VoyageContributingPerCaptainScalar {
  value: number;
  shap: number;
}

/** `perCaptain` entry when `shape === "perCaptainNamed"` (captain / manning / nationality style factors). */
export interface VoyageContributingPerCaptainNamed {
  name: string;
  value: number;
  shap: number;
  /** Dim crew id from captain slot dropdown merge (optional; drives `feature_value_ui_temp` in Inference payload). */
  uiSelectedDimCrewId?: number | null;
}

export type VoyageContributingFactorShape = 'scalar' | 'perCaptainScalar' | 'perCaptainNamed';

/**
 * One enriched factor row under `factors.captain` | `crew` | `ship` | `voyage`.
 * Optional fields depend on `shape` (see backend OpenAPI / voyage contributing factors service).
 */
export interface VoyageContributingFactorRow {
  featureId: number;
  apiName: string;
  uiName: string;
  description: string;
  category: string;
  isEditable: boolean;
  shape: VoyageContributingFactorShape;
  aggregateValue: number;
  scalarValueUi?: string | null;
  aggregateShap?: number | null;
  /** Rare alternate API field; prefer `aggregateShap` when both exist. */
  shap?: number | null;
  /** Captain id → scalar or named breakdown. */
  perCaptain?: Readonly<
    Record<string, VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed>
  >;
}

/** Factor buckets per voyage. */
export interface VoyageContributingFactorsBuckets {
  captain: VoyageContributingFactorRow[];
  crew: VoyageContributingFactorRow[];
  ship: VoyageContributingFactorRow[];
  voyage: VoyageContributingFactorRow[];
}

/** Single voyage object inside `data.voyages[]`. */
export interface VoyageContributingFactorsVoyage {
  voyageId: string;
  voyageNo: string;
  shipCode: string;
  voyageLevelRisk: number;
  voyageLevelRiskCategory: string | null;
  reasonNotPredicted: string | null;
  /** Human-readable reason descriptions for unpredicted voyages (one entry per reason). */
  reasonNotPredictedDescriptionList: string[] | null;
  /** Diagnostic / log-style reason descriptions from inference `ReasonDescriptionLogsList`. */
  reasonDescriptionLogsList: string[] | null;
  /**
   * `false` when any reason in `reasonNotPredicted` has `isEditable: false` in `DTB_REASONS_MASTER_DATA`.
   * Predicted voyages (no reasons) are always `true`.
   */
  voyageFactorsEditable: boolean;
  /**
   * `true` when the only reason blocking prediction is `NO_CAPTAIN_ASSIGNED`.
   * The factors section is still rendered so the user can assign a captain.
   */
  voyageNoCaptainAssigned: boolean;
  captainWeightDict: Readonly<Record<string, number>>;
  /** Positional captain ids from `CaptainCrewIdDict` (`"1"` → current captain DimCaptainId, etc.). */
  captainCrewIdDict: Readonly<Record<string, number>>;
  /**
   * Captain assignment window from `CaptainCrewIdTimeline`
   * (`DimCaptainId` → `{ start_date, end_date }` as `YYYY-MM-DD`).
   */
  captainCrewIdTimeline?: CaptainCrewIdTimeline | null;
  baseValue: number | null;
  captains: VoyageContributingCaptain[];
  factors: VoyageContributingFactorsBuckets;
}

/** "Last edited" details from the latest saved summary-of-changes record for the ship. */
export interface VoyageContributingFactorsLastEdited {
  userName: string | null;
  userDisplayName: string | null;
  userInitials: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface VoyageContributingFactorsApiData {
  /** Latest saved edit metadata; `null`/absent when the ship has no saved changes yet. */
  lastEdited?: VoyageContributingFactorsLastEdited | null;
  voyages: VoyageContributingFactorsVoyage[];
}

export interface VoyageContributingFactorsResponse {
  success: boolean;
  message: string;
  data: VoyageContributingFactorsApiData;
}

/** Resolve one voyage from the contributing-factors payload by tab id (`voyageId`). */
export function contributingFactorsVoyageById(
  data: VoyageContributingFactorsApiData | null | undefined,
  voyageId: string | number,
): VoyageContributingFactorsVoyage | null {
  if (!data?.voyages?.length) {
    return null;
  }
  const key = String(voyageId);
  const found = data.voyages.find((v) => String(v.voyageId) === key);
  return found ?? null;
}

/** Find one factor row anywhere in the voyage buckets (`captain` | `crew` | `ship` | `voyage`). */
function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function perCaptainEntryShap(entry: unknown): number | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  return toFiniteNumber((entry as { shap?: unknown }).shap);
}

/**
 * Single scalar SHAP-style value for risk display: `aggregateShap`, root `shap`, or a blend of `perCaptain[].shap`
 * (weighted by `captainWeightDict` when every captain with shap has a weight; otherwise mean).
 */
export function voyageContributingFactorRiskShapScalar(
  row: VoyageContributingFactorRow | null | undefined,
  voyage: VoyageContributingFactorsVoyage | null | undefined,
): number | null {
  if (!row) {
    return null;
  }
  const fromAgg = toFiniteNumber(row.aggregateShap);
  if (fromAgg !== null) {
    return fromAgg;
  }
  const fromRoot = toFiniteNumber(row.shap);
  if (fromRoot !== null) {
    return fromRoot;
  }

  const pc = row.perCaptain;
  if (!pc || typeof pc !== 'object') {
    return null;
  }

  const captainIds = Object.keys(pc).filter((id) => perCaptainEntryShap(pc[id]) !== null);
  if (captainIds.length === 0) {
    return null;
  }

  const weights = voyage?.captainWeightDict ?? {};
  const allHaveWeight = captainIds.every((id) => toFiniteNumber(weights[id]) !== null);
  if (allHaveWeight) {
    let sum = 0;
    for (const id of captainIds) {
      const w = toFiniteNumber(weights[id])!;
      const s = perCaptainEntryShap(pc[id])!;
      sum += w * s;
    }
    return sum;
  }

  const vals = captainIds.map((id) => perCaptainEntryShap(pc[id])!);
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

const FACTOR_VALUE_NUMBER_FORMAT = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

/** Format finite numbers for factor value lines (max 2 decimal places). */
export function formatContributingFactorNumericDisplay(n: number): string {
  return FACTOR_VALUE_NUMBER_FORMAT.format(n);
}

/** If `s` is a numeric string, format to max 2 decimals; otherwise return unchanged (labels, dates, etc.). */
export function formatContributingFactorScalarUiDisplay(s: string): string {
  const t = s.trim();
  if (t === '') {
    return s;
  }
  const n = Number(t);
  if (!Number.isFinite(n)) {
    return s;
  }
  return formatContributingFactorNumericDisplay(n);
}

function perCaptainEntryDisplayValue(entry: unknown): string | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const o = entry as { name?: unknown; value?: unknown };
  if (typeof o.name === 'string' && o.name.trim() !== '') {
    return o.name.trim();
  }
  const v = o.value;
  if (typeof v === 'number' && Number.isFinite(v)) {
    return formatContributingFactorNumericDisplay(v);
  }
  if (v != null && v !== '') {
    const s = String(v).trim();
    if (s === '') {
      return null;
    }
    return formatContributingFactorScalarUiDisplay(s);
  }
  return null;
}

/** Human-readable factor value: per-captain entry when `captainId` matches, else scalar / aggregate. */
export function voyageContributingFactorValueDisplay(
  row: VoyageContributingFactorRow | null | undefined,
  captainId: string | null | undefined,
): string {
  if (!row) {
    return '—';
  }
  const cid = captainId != null ? String(captainId).trim() : '';
  if (cid && row.perCaptain && typeof row.perCaptain === 'object') {
    const entry = row.perCaptain[cid];
    const pc = perCaptainEntryDisplayValue(entry);
    if (pc !== null) {
      return pc;
    }
  }
  const ui = row.scalarValueUi != null ? String(row.scalarValueUi).trim() : '';
  if (ui !== '') {
    return formatContributingFactorScalarUiDisplay(ui);
  }
  if (typeof row.aggregateValue === 'number' && Number.isFinite(row.aggregateValue)) {
    return formatContributingFactorNumericDisplay(row.aggregateValue);
  }
  return '—';
}

/**
 * Same resolution order as `parseScalarNumber` in `contributing-factors-form-patch.ts`
 * (`scalarValueUi` before `aggregateValue`).
 */
function parseScalarNumberFromRow(row: VoyageContributingFactorRow): number | null {
  if (row.scalarValueUi != null && String(row.scalarValueUi).trim() !== '') {
    const n = Number(String(row.scalarValueUi).replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }
  const a = row.aggregateValue;
  if (typeof a === 'number' && !Number.isNaN(a)) {
    return a;
  }
  return null;
}

/** Mirrors `perCaptainNumeric` in `contributing-factors-form-patch.ts`. */
function perCaptainNumericFromRow(row: VoyageContributingFactorRow, captainId: string): number | null {
  const entry = row.perCaptain?.[captainId];
  if (entry && typeof entry === 'object' && 'value' in entry) {
    const v = (entry as { value: unknown }).value;
    if (typeof v === 'number' && !Number.isNaN(v)) {
      return v;
    }
  }
  if (row.shape === 'scalar') {
    return parseScalarNumberFromRow(row);
  }
  return null;
}

/**
 * Raw numeric using the same rules as `buildContributingFactorsFormPatch` so “Current:” time splits match
 * slider/input totals after hydration.
 */
export function voyageContributingFactorNumericRaw(
  row: VoyageContributingFactorRow | null | undefined,
  captainId: string | null | undefined,
): number | null {
  if (!row) {
    return null;
  }
  const cid = captainId != null ? String(captainId).trim() : '';
  if (cid) {
    return perCaptainNumericFromRow(row, cid);
  }
  return parseScalarNumberFromRow(row);
}

/**
 * SHAP for one captain slot when `perCaptain[captainId].shap` exists; otherwise same aggregate logic as
 * {@link voyageContributingFactorRiskShapScalar}.
 */
export function voyageContributingFactorRiskShapForCaptain(
  row: VoyageContributingFactorRow | null | undefined,
  voyage: VoyageContributingFactorsVoyage | null | undefined,
  captainId: string | null | undefined,
): number | null {
  if (!row) {
    return null;
  }
  const cid = captainId != null ? String(captainId).trim() : '';
  if (cid && row.perCaptain && typeof row.perCaptain === 'object') {
    const entry = row.perCaptain[cid];
    const s = perCaptainEntryShap(entry);
    if (s !== null) {
      return s;
    }
  }
  return voyageContributingFactorRiskShapScalar(row, voyage);
}

export function contributingFactorRowByFeatureId(
  voyage: VoyageContributingFactorsVoyage | null | undefined,
  featureId: number,
): VoyageContributingFactorRow | null {
  if (!voyage?.factors) {
    return null;
  }
  const buckets: readonly VoyageContributingFactorRow[][] = [
    voyage.factors.captain ?? [],
    voyage.factors.crew ?? [],
    voyage.factors.ship ?? [],
    voyage.factors.voyage ?? [],
  ];
  for (const list of buckets) {
    const hit = list.find((r) => r.featureId === featureId);
    if (hit) {
      return hit;
    }
  }
  return null;
}

/** Options for roster list mapping (positional order from API `captainCrewIdDict`). */
export interface MapContributingCaptainsToListItemsOptions {
  /** `"1"` → current captain DimCaptainId; higher positions = next in rotation. */
  crewIdDict?: Readonly<Record<string, number>> | null;
}

/**
 * Formats a `YYYY-MM-DD` (or parseable) date as `02 Feb 2026` (en-GB, UTC).
 * Returns `null` when the input cannot be parsed.
 */
export function formatCaptainTimelineDisplayDate(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  let date: Date;
  if (iso) {
    date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  } else {
    date = new Date(trimmed);
  }
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Looks up `captainCrewIdTimeline[captainId]` and returns
 * `(from 02 Feb 2026 to 01 Mar 2026)`, or `null` when dates are missing.
 */
export function formatCaptainCrewTimelineRange(
  timeline: CaptainCrewIdTimeline | null | undefined,
  captainId: string | number | null | undefined,
): string | null {
  if (captainId == null || captainId === '') return null;
  if (!timeline || typeof timeline !== 'object' || Array.isArray(timeline)) return null;
  const entry = timeline[String(captainId)];
  if (!entry || typeof entry !== 'object') return null;
  const start = formatCaptainTimelineDisplayDate(entry.start_date);
  const end = formatCaptainTimelineDisplayDate(entry.end_date);
  if (!start || !end) return null;
  return `(from ${start} to ${end})`;
}

/**
 * Invert `captainCrewIdDict` / `CaptainCrewIdDict` into DimCaptainId → position (`1` = current).
 */
export function captainPositionByDimIdFromCrewIdDict(
  dict: Readonly<Record<string, number>> | null | undefined,
): ReadonlyMap<string, number> {
  const result = new Map<string, number>();
  if (!dict || typeof dict !== 'object') {
    return result;
  }
  for (const [position, captainId] of Object.entries(dict)) {
    const pos = Number(position);
    const id = Number(captainId);
    if (Number.isFinite(pos) && Number.isFinite(id)) {
      result.set(String(id), pos);
    }
  }
  return result;
}

function rosterOrderFromCrewIdDict(
  captainId: string,
  entry: VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed | undefined,
  positionByDimId: ReadonlyMap<string, number>,
): number | null {
  const dimKeys: string[] = [String(captainId)];
  if (entry && 'uiSelectedDimCrewId' in entry) {
    const dim = toFiniteNumber(entry.uiSelectedDimCrewId);
    if (dim != null) {
      dimKeys.unshift(String(dim));
    }
  }
  for (const key of dimKeys) {
    const pos = positionByDimId.get(key);
    if (pos != null && Number.isFinite(pos)) {
      return pos;
    }
  }
  return null;
}

/** Roster array or `perCaptain` map (`captainId` → named/scalar breakdown from a factor row). */
type MapContributingCaptainsToListItemsInput =
  | readonly VoyageContributingCaptain[]
  | Readonly<Record<string, VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed>>;

function isPerCaptainBreakdownMap(
  input: MapContributingCaptainsToListItemsInput,
): input is Readonly<Record<string, VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed>> {
  return typeof input === 'object' && input !== null && !Array.isArray(input);
}

/**
 * Map API roster rows or a factor’s `perCaptain` record to ship-overview `CaptainListItem`.
 * When `options.crewIdDict` is set, order follows positional ids (`"1"` = current captain);
 * otherwise `perCaptain` falls back to descending |shap|, and `captains[]` uses each row’s `order`.
 */
export function mapContributingCaptainsToListItems(
  captains: MapContributingCaptainsToListItemsInput | null | undefined,
  options?: MapContributingCaptainsToListItemsOptions,
): CaptainListItem[] {
  if (captains == null) {
    return [];
  }
  const positionByDimId = captainPositionByDimIdFromCrewIdDict(options?.crewIdDict);
  const useCrewOrder = positionByDimId.size > 0;

  if (isPerCaptainBreakdownMap(captains)) {
    const entries = Object.entries(captains) as [
      string,
      VoyageContributingPerCaptainScalar | VoyageContributingPerCaptainNamed,
    ][];
    const sorted = useCrewOrder
      ? [...entries].sort(([idA, a], [idB, b]) => {
          const posA = rosterOrderFromCrewIdDict(idA, a, positionByDimId) ?? 999;
          const posB = rosterOrderFromCrewIdDict(idB, b, positionByDimId) ?? 999;
          return posA - posB;
        })
      : [...entries].sort(([, a], [, b]) => {
          const shapA = Math.abs(toFiniteNumber(a.shap) ?? 0);
          const shapB = Math.abs(toFiniteNumber(b.shap) ?? 0);
          return shapB - shapA;
        });
    return sorted.map(([captainId, entry], index) => {
      const nameFromEntry =
        'name' in entry && typeof entry.name === 'string' && entry.name.trim().length > 0
          ? entry.name.trim()
          : '';
      const order = useCrewOrder
        ? (rosterOrderFromCrewIdDict(captainId, entry, positionByDimId) ?? 999)
        : index + 1;
      return {
        captainId,
        name: nameFromEntry || captainId,
        nationality: '',
        manningAgent: '',
        order,
      };
    });
  }
  return [...captains]
    .map((c) => {
      const orderFromDict = useCrewOrder
        ? rosterOrderFromCrewIdDict(c.captainId, undefined, positionByDimId)
        : null;
      return {
        captainId: c.captainId,
        name: c.name,
        nationality: c.nationality,
        manningAgent: c.manningAgent,
        order: orderFromDict ?? c.order ?? 999,
      };
    })
    .sort((a, b) => (Number(a.order) || 999) - (Number(b.order) || 999));
}

/** Captain roster factor used for slot ids / labels (overview, form patch, inference). */
export const CAPTAIN_CREW_RATIO_WEIGHTED_API_NAME =
  'CaptainCrew_AccidentPerVoyageRatio_weighted' as const;

/**
 * Captain slot roster for one voyage: prefer latest `perCaptain` on the crew-ratio captain factor,
 * otherwise `voyage.captains`. When both exist, merge manning/nationality from `voyage.captains` by id.
 */
export function contributingFactorsCaptainRosterForSlots(
  voyage: VoyageContributingFactorsVoyage | null | undefined,
): CaptainListItem[] {
  if (!voyage?.factors) {
    return [];
  }
  const crewIdDict = voyage.captainCrewIdDict;
  const positionByDimId = captainPositionByDimIdFromCrewIdDict(crewIdDict);
  const mapOptions: MapContributingCaptainsToListItemsOptions | undefined =
    positionByDimId.size > 0 ? { crewIdDict } : undefined;

  const perCaptain = voyage.factors.captain?.find(
    (r) => r.apiName === CAPTAIN_CREW_RATIO_WEIGHTED_API_NAME,
  )?.perCaptain;
  const fromPayload =
    perCaptain && Object.keys(perCaptain).length > 0
      ? mapContributingCaptainsToListItems(perCaptain, mapOptions)
      : mapContributingCaptainsToListItems(voyage.captains ?? [], mapOptions);
  if (!fromPayload.length) {
    return [];
  }
  let merged: CaptainListItem[];
  if (voyage.captains?.length) {
    const byId = new Map(voyage.captains.map((c) => [String(c.captainId), c]));
    merged = fromPayload.map((item) => {
      const r = byId.get(String(item.captainId));
      if (!r) {
        return item;
      }
      const orderFromDict = rosterOrderFromCrewIdDict(
        String(item.captainId),
        undefined,
        positionByDimId,
      );
      const rosterOrder =
        orderFromDict ??
        (r.order != null && Number.isFinite(Number(r.order)) ? Number(r.order) : item.order);
      return {
        ...item,
        nationality: r.nationality ?? item.nationality,
        manningAgent: r.manningAgent ?? item.manningAgent,
        order: rosterOrder,
      };
    });
  } else {
    merged = fromPayload;
  }
  return [...merged].sort((a, b) => (Number(a.order) || 999) - (Number(b.order) || 999));
}
